import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Button, Typography } from "@mui/material";
import { type FC, type FormEvent, useState } from "react";

import { FilterMenuTitle } from "~/components/UI/FilterMenuTitle";
import { RichTooltip } from "~/components/UI/RichTooltip";

import * as Styled from "../ActivityFilter24h.styles";
import {
  type IActivityFilter24hValue,
  PRICE_CHANGE_24H_TOOLTIP_TEXT,
  PRICE_CHANGE_PRESETS,
  VOLUME_24H_TOOLTIP_TEXT,
} from "../constants";
import { ActivityNumberInput } from "./ActivityNumberInput";

interface ActivityFilter24hFormProps {
  initialValue: IActivityFilter24hValue;
  onApply: (value: IActivityFilter24hValue) => void;
}

export const ActivityFilter24hForm: FC<ActivityFilter24hFormProps> = ({
  initialValue,
  onApply,
}) => {
  const [volume, setVolume] = useState(initialValue.minVolume24h ?? null);
  const [priceMin, setPriceMin] = useState<number | null>(
    initialValue.minPriceChange24h != null ? initialValue.minPriceChange24h * 100 : null
  );
  const [priceMax, setPriceMax] = useState<number | null>(
    initialValue.maxPriceChange24h != null ? initialValue.maxPriceChange24h * 100 : null
  );

  const [showRangeError, setShowRangeError] = useState(false);

  const hasRangeError =
    priceMin != null && priceMax != null && priceMin > priceMax;

  const activePreset = priceMax == null ? priceMin : null;

  const handlePriceMinChange = (value: number | null) => {
    setShowRangeError(false);
    setPriceMin(value);
  };

  const handlePriceMaxChange = (value: number | null) => {
    setShowRangeError(false);
    setPriceMax(value);
  };

  const selectPreset = (preset: number | null) => {
    if (preset == null) return;

    setShowRangeError(false);
    setPriceMin(preset);
    setPriceMax(null);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (hasRangeError) {
      setShowRangeError(true);
      return;
    }

    onApply({
      minVolume24h: volume ?? undefined,
      minPriceChange24h: priceMin ?? undefined,
      maxPriceChange24h: priceMax ?? undefined,
    });
  };

  return (
    <Styled.FormContainer noValidate onSubmit={handleSubmit}>
      <FilterMenuTitle>24h Activity</FilterMenuTitle>

      <Styled.Section>
        <Styled.SectionHeader>
          <Typography variant="caption">Min 24h Volume</Typography>
          <RichTooltip title={VOLUME_24H_TOOLTIP_TEXT} maxWidth={280}>
            <Styled.InfoIconWrapper>
              <InfoOutlinedIcon fontSize="inherit" color="disabled" />
            </Styled.InfoIconWrapper>
          </RichTooltip>
        </Styled.SectionHeader>

        <ActivityNumberInput
          value={volume}
          onChange={setVolume}
          placeholder="10,000"
          prefix="$"
        />
      </Styled.Section>

      <Styled.Section>
        <Styled.SectionHeader>
          <Typography variant="caption">24h Price Change (%)</Typography>
          <RichTooltip title={PRICE_CHANGE_24H_TOOLTIP_TEXT} maxWidth={280}>
            <Styled.InfoIconWrapper>
              <InfoOutlinedIcon fontSize="inherit" color="disabled" />
            </Styled.InfoIconWrapper>
          </RichTooltip>
        </Styled.SectionHeader>

        <Styled.PriceChangeRow>
          <ActivityNumberInput
            value={priceMin}
            onChange={handlePriceMinChange}
            placeholder="Min"
            suffix="%"
            allowNegative
            error={showRangeError}
          />

          <Typography variant="caption" sx={{
            color: "text.secondary"
          }}>
            –
          </Typography>

          <ActivityNumberInput
            value={priceMax}
            onChange={handlePriceMaxChange}
            placeholder="Max"
            suffix="%"
            allowNegative
            error={showRangeError}
          />
        </Styled.PriceChangeRow>

        {showRangeError && (
          <Typography variant="caption" color="error">
            Min must be less than or equal to Max
          </Typography>
        )}

        <Styled.ToggleGroup
          exclusive
          size="small"
          value={activePreset}
          onChange={(_, preset) => selectPreset(preset)}
        >
          {PRICE_CHANGE_PRESETS.map(({ label, value }) => (
            <Styled.PillButton key={value} value={value}>
              <Typography variant="caption">{label}</Typography>
            </Styled.PillButton>
          ))}
        </Styled.ToggleGroup>
      </Styled.Section>

      <Button variant="contained" size="small" type="submit">
        Apply
      </Button>
    </Styled.FormContainer>
  );
};
